<template lang="pug">
bottom-modal(:id="modalId", width="w-xtra-large", :loading="onLoading('save-product')")
    span(slot="header-text") {{ product !== null && productExists ? 'Update' : 'Create' }} Product
    div(slot="body")
        div.container
            form(id="product-form", @submit.prevent="submit", v-if="product != null")
                fieldset(:disabled="onLoading('save-product')")
                    div.row
                        div.col-md-6.p-l-md.p-t-md.p-r-md
                            h6.m-b-md Details

                            div.row.dz-container
                                div.col-xs-5.col-sm-4
                                    div.box
                                        dropzone(
                                            v-show="product.media_urls.length == 0",
                                            ref="dropzone",
                                            id="itemImage",
                                            paramName="media",
                                            :language="dropzoneLanguange",
                                            :url="api_media",
                                            :acceptedFileTypes="'image/*'",
                                            :maxNumberOfFiles="1",
                                            v-on:vdropzone-sending="onSendingFile",
                                            v-on:vdropzone-success="onUploadSuccess",
                                            v-on:vdropzone-thumbnail="onThumbnail",
                                            :autoProcessQueue="false"
                                        )

                                        div.dz-custom-image-preview(
                                            v-if="product.media_urls.length > 0"
                                        )
                                            img(
                                                :src="product.media_urls[0].original",
                                                alt="Product image"
                                            )
                                            span.remove-icon(
                                                @click="removeImage"
                                            )
                                div.col-xs-7.col-sm-8.pro-tip-container
                                    h6 Pro Tip:
                                    ul
                                        li Please ensure the image is emotionally engaging.
                                        li Keep the image background clear.
                                        li It is encouraged to upload a clear image.

                            div.row
                                div.col-xs-12
                                    div.md-form-group
                                        input.md-input(
                                            type="text",
                                            v-model="product.code",
                                            :disabled="canOnlyManageImages"
                                        )
                                        label Barcode

                            div.row
                                div.col-xs-12
                                    div.md-form-group
                                        input.md-input(
                                            type="text",
                                            v-model="product.name",
                                            placeholder="Required",
                                            required,
                                            :disabled="canOnlyManageImages"
                                        )
                                        label Product Name

                            div.row
                                div.col-xs-12
                                    div.md-form-group
                                        select.md-input(
                                            v-model="product.price_type"
                                            @change="isTypeFixed && !hasVariant ? addVariant() : ''",
                                            :disabled="onLoading('save-product') || !canManageItems || canOnlyManageImages"
                                        )
                                            option(
                                                v-for="pricing in pricingType",
                                                v-bind:value="pricing.id"
                                            ) {{ pricing.name }}
                                        label Pricing

                            div.row(v-show="isTypeUnitPrice")
                                div.col-xs-6
                                    div.md-form-group
                                        input.md-input(
                                            type="text",
                                            v-model="product.unit_price",
                                            data-parsley-type="number",
                                            min="0",
                                            required,
                                            :disabled="canOnlyManageImages"
                                        )
                                        label Unit Price

                                div.col-xs-6
                                    div.md-form-group
                                        input.md-input(
                                            type="text",
                                            v-model="product.cost",
                                            data-parsley-type="number",
                                            min="0",
                                            :disabled="canOnlyManageImages"
                                        )
                                        label Cost

                            //- PS-10730: what one item is (piece, bottle, kg). Optional; the approved
                            //- list comes from the server so it cannot drift from validation.
                            div.row
                                div.col-xs-6
                                    div.md-form-group
                                        select.md-input(
                                            v-model="product.uom",
                                            :disabled="canOnlyManageImages"
                                        )
                                            option(:value="null") Not set
                                            option(
                                                v-for="unit in uomOptions",
                                                :key="unit.code",
                                                :value="unit.code"
                                            ) {{ unit.name }}
                                        label Unit of measure
                                div.col-xs-6
                                    p.text-muted.m-t-md What one item is, for example piece, bottle or kg.

                            //- The pack: how many single units it holds and what it is called. Units per
                            //- pack cannot be saved without a pack type and a single unit (server rule).
                            div.row
                                div.col-xs-6
                                    div.md-form-group
                                        select.md-input(
                                            v-model="product.pack_uom",
                                            :disabled="canOnlyManageImages"
                                        )
                                            option(:value="null") Not set
                                            option(
                                                v-for="unit in uomOptions",
                                                :key="'pack-' + unit.code",
                                                :value="unit.code"
                                            ) {{ unit.name }}
                                        label Pack type
                                div.col-xs-6
                                    div.md-form-group
                                        input.md-input(
                                            type="number",
                                            v-model.number="product.pack_quantity",
                                            min="2",
                                            max="1000",
                                            step="1",
                                            placeholder="Optional",
                                            :disabled="canOnlyManageImages"
                                        )
                                        label Units per pack
                            div.row
                                div.col-xs-12
                                    p.text-muted(style="margin-top:-8px") For example 12 × bottle = carton. Units per pack needs a pack type and a unit of measure.

                            product-variants(
                                :product="product",
                                v-show="isTypeFixed",
                                ref="productVariant",
                                :disabled="canOnlyManageImages"
                            )

                        div.col-md-6.p-a-md
                            h6.m-b-md Options

                            div.row
                                div.col-xs-12
                                    div.md-form-group(v-if="! productExists")
                                        select.md-input(
                                            v-model="product.outlet_id",
                                            :disabled="productExists || canOnlyManageImages"
                                        )
                                            option(
                                                v-if="canAccessAllOutlets"
                                                :value="0"
                                            ) {{ trans('product.apply_to_all_outlets') }}
                                            option(
                                                v-for="outlet in outletList",
                                                v-bind:value="outlet.id"
                                            ) {{ outlet.name }}
                                        label {{ trans('general.outlet') }}
                                    div.m-b-sm(v-else)
                                        span.clear.text-muted {{ trans('general.outlet') }}
                                        | {{ productOutlet ? productOutlet : trans('product.visible_to_all_outlets') }}

                            div.row
                                div.col-xs-12
                                    div.form-group
                                        category-selector(v-model="product.category", :disabled="canOnlyManageImages")

                            div.row
                                div.col-xs-12
                                    div.form-group
                                        tag-selector(v-model="product.tags", :disabled="canOnlyManageImages")

                            div.row
                                div.col-xs-12
                                    div.form-group
                                        modifier-selector(
                                            v-model="product.modifier_sets",
                                            :tag-modifiers="fixedModifiers",
                                            :disabled="canOnlyManageImages"
                                        )

                            div.row
                                div.col-xs-12
                                    div.form-group
                                        label.typo__label.multiselect-label Taxes
                                        multiselect(
                                            v-model="product.taxes",
                                            label="name",
                                            track-by="id",
                                            placeholder="Type to search",
                                            :options="taxList",
                                            :multiple="true",
                                            :close-on-select="false",
                                            :hide-selected="true",
                                            :max-height="170",
                                            :limit="5",
                                            :disabled="canOnlyManageImages"
                                        )

                            div.row
                                div.col-xs-12.p-t-sm
                                    div.md-form-group
                                        textarea(
                                            rows="2",
                                            class="md-input",
                                            v-model="product.description",
                                            :disabled="canOnlyManageImages"
                                        )
                                        label Description

                            div.row
                                div.col-xs-12
                                    label Options

                                    .form-group
                                        p
                                            label.md-switch
                                                input(
                                                    v-model="product.allow_media_override",
                                                    type="checkbox",
                                                    :disabled="canOnlyManageImages"
                                                )
                                                i.indigo
                                                | {{ trans('product.allow_media_override') }}

                                        p
                                            small {{ trans('product.allow_media_override_description') }}

                                    div.form-group
                                        p
                                            label.md-switch
                                                input(
                                                    v-model="status",
                                                    type="checkbox",
                                                    :disabled="canOnlyManageImages"
                                                )
                                                i.indigo
                                                | Hide product
                                        p
                                            small {{ trans('product.the_product_will_be_hidden_from_central_and_waiter') }}

                                    .form-group
                                        p
                                            label.md-switch
                                                input(
                                                    v-model="product.chargeable",
                                                    type="checkbox",
                                                    :disabled="canOnlyManageImages"
                                                )
                                                i.indigo
                                                | Apply charges
                                        p
                                            small Applicable charges will be applied to the product during checkout

                        //-
                            div.row
                                div.col-xs-12
                                    div.form-group
                                        label Central Display Options
                                        br
                                        color_picker(
                                            :color="product.color_code",
                                            id="_colorpicker",
                                            ref="colorPicker"
                                        )

    button.btn.btn-fw.primary(
        slot="footer",
        @click="submit()",
        :disabled="onLoading('save-product') || !(canManageItems || canOnlyManageImages)"
    )
        v-loading(loader="save-product") Save

    label.md-switch(slot="popup" v-if="!productExists")
        input(type='checkbox', v-model="toggle")
        i.indigo
        | Keep form open

</template>

<script>
import Auth from '@js/plugins/auth';
import AuthMixin from '@js/mixins/auth';
import modifierSelector from './modifier-selector.vue';
import tagSelector from './tag-selector.vue';
import categorySelector from './category-selector.vue';
import productVariants from './product-variants.vue';
import { mapGetters, mapActions } from 'vuex';
import Feature from '@js/plugins/feature';

export default {
    components: {
        modifierSelector,
        tagSelector,
        productVariants,
        categorySelector,
    },

    mixins: [AuthMixin],

    data() {
        let pricingType = [];
        let initialPriceType = 0;

        if (Feature.hasFeature('fuel')) {
            pricingType = [
                { name: 'Unit', id: 1 },
                { name: 'Variable', id: 2 },
            ];

            initialPriceType = 1;
        } else {
            pricingType = [
                { name: 'Fixed', id: 0 },
                { name: 'Unit', id: 1 },
                { name: 'Variable', id: 2 },
            ];
        }

        return {
            uomOptions: [],
            toggle: false,
            mediaId: 0,
            remove_media: false,
            pricingType,
            product: null,
            initialPriceType,
            isValidImageResolution: false,
            dropzoneLanguange: {
                dictDefaultMessage:
                    '<br>Drop files or click here to upload<br><div class="dz-spec-text">Max size: 2MiB<br>Resolution: 1080 x 1080</div>',
                dictCancelUpload: '',
                dictCancelUploadConfirmation:
                    'Are you sure you want to cancel this upload?',
                dictFallbackMessage:
                    'Your browser does not support drag and drop file uploads.',
                dictFallbackText:
                    'Please use the fallback form below to upload your files like in the olden days.',
                dictFileTooBig:
                    'File is too big ({{filesize}}MiB). Max filesize: {{maxFilesize}}MiB.',
                dictInvalidFileType: "You can't upload files of this type.",
                dictMaxFilesExceeded:
                    'You can not upload any more files. (max: {{maxFiles}})',
                dictRemoveFile: '',
                dictRemoveFileConfirmation: null,
                dictResponseError: 'Server responded with {{statusCode}} code.',
            },
        };
    },

    computed: {
        activeOutlet() {
            let ao = this.$store.getters.activeOutlet;

            if (this.canAccessAllOutlets && ao === 0) {
                return ao;
            }

            if (_.includes(Auth.outlets, ao)) {
                return ao;
            }

            return this.firstAvailableOutlet();
        },

        canManageItems() {
            return Auth.hasAnyPermission(
                'franchise_manage_items',
                'franchise_manage_products',
            );
        },

        canOnlyManageImages() {
            return (
                !this.canManageItems &&
                Auth.hasPermissionTo('franchise_manage_product_images')
            );
        },

        modalId() {
            return 'item-popup';
        },

        api_media() {
            return Laroute.route('franchise.inventory.item.addmedia');
        },

        ...mapGetters(['activeOutlet', 'outletList', 'findOutletById']),

        ...mapGetters('tax', ['taxList', 'taxAutoAssignProductList']),

        isTypeFixed() {
            return this.product.price_type == 0;
        },

        isTypeUnitPrice() {
            return this.product.price_type == 1;
        },

        productExists() {
            if (this.product) {
                return this.product.id != 0 && this.product.id != undefined;
            }

            return false;
        },

        hasVariant() {
            return this.product.variants.length > 0;
        },

        productOutlet() {
            const outlet = this.findOutletById(this.product.outlet_id);

            if (_.isUndefined(outlet)) {
                return null;
            }

            return outlet.name;
        },

        status: {
            get() {
                return this.product.status == 0;
            },

            set(value) {
                this.product.status = value ? 0 : 1;
            },
        },

        fixedModifiers() {
            let m = [];

            if (this.product) {
                let tags = this.product.tags;
                tags.forEach(t => {
                    if (t.modifier_sets) {
                        t.modifier_sets.forEach(n => m.push(n));
                    }
                });
            }

            return _.uniqBy(m, 'id');
        },
    },

    watch: {
        toggle(data) {
            localStorage.setItem('toggle', data);
        },
    },

    mounted() {
        if (localStorage.getItem('toggle')) {
            this.toggle = JSON.parse(localStorage.getItem('toggle'));
        }
    },

    created() {
        this.getTaxList();
        this.getCategories();
        this.getUomOptions();
        this.registerEventBus();
    },

    methods: {
        ...mapActions('product', [
            'getProduct',
            'updateProduct',
            'createProduct',
        ]),

        ...mapActions('category', ['getCategories']),

        ...mapActions('tax', ['getTaxList']),

        setProduct(product) {
            this.product = product;
        },
        // PS-10730: the approved units of measure, code + label, from config('product.uom').
        getUomOptions() {
            return axios
                .get(Laroute.route('franchise.inventory.item.uom'))
                .then(({ data }) => {
                    this.uomOptions = data.data || [];
                })
                .catch(err => {
                    // Without the list the 2 selects offer only "Not set", so say so rather than
                    // look like an empty configuration (MR review: no catch-and-ignore).
                    this.uomOptions = [];
                    console.error('Failed to load the units of measure', err);
                    this.showApiError(
                        err,
                        'Could not load the unit list. Reload the page to try again.',
                    );
                });
        },

        submit() {
            if (this.canOnlyManageImages && !this.productExists) {
                return;
            }

            if (!this.validateForm('product-form')) {
                return;
            }

            if (this.hasFileUploaded() && !this.isValidImageResolution) {
                return;
            }

            if (this.hasRejectedFile()) {
                return;
            }

            this[this.productExists ? 'updateProduct' : 'createProduct'](
                this.mapProductValue(),
            )
                .then(res => {
                    let data = res.data.data;

                    if (this.hasFileToUpload(data.id)) {
                        this.uploadFile(data.id);
                    } else {
                        this.successCallback();
                    }
                })
                .catch(err => {
                    this.showApiError(err);
                });
        },

        addVariant: function() {
            this.$refs.productVariant.addVariant();
        },

        mapProductValue() {
            const product = _.pick(this.product, [
                'id',
                'name',
                'code',
                'price_type',
                'outlet_id',
                'description',
                'unit_price',
                'cost',
                'uom',
                'pack_uom',
                'pack_quantity',
                'status',
                'chargeable',
                'allow_media_override',
                // 'track',
            ]);

            return Object.assign(product, {
                variants: this.getVariants(),
                tags: this.product.tags.map(obj => obj.id),
                modifier_sets: this.product.modifier_sets.map(obj => obj.id),
                /*
                color_code: this.$refs.colorPicker.color_code,
                */
                remove_media: this.remove_media,
                taxes: this.product.taxes.map(obj => obj.id),
                category_id: !_.isNull(this.product.category)
                    ? this.product.category.id
                    : null,
            });
        },

        getVariants() {
            if (this.isTypeFixed) {
                return this.sortVariantBySequence(this.product.variants, true);
            } else {
                return [];
            }
        },

        sortVariantBySequence(variants, is_submitted = false) {
            let v;

            if (!is_submitted) {
                v = _.sortBy(variants, v => v.sequence);
            } else {
                v = variants;
            }

            v.forEach((v, idx) => {
                this.$set(v, 'sequence', ++idx);
            });

            return v;
        },

        onThumbnail(file) {
            const requiredWidth = 1080;
            const requiredHeight = 1080;
            if (
                file.width !== requiredWidth ||
                file.height !== requiredHeight
            ) {
                this.$refs.dropzone.error(
                    file,
                    `Image must be ${requiredWidth} x ${requiredHeight} pixels.`,
                );
                this.isValidImageResolution = false;
                return;
            }

            this.isValidImageResolution = true;
        },

        onSendingFile(file, xhr, formData) {
            formData.append('id', this.mediaId);
        },

        onUploadSuccess(file, response) {
            this.successCallback();
            this.$refs.dropzone.removeAllFiles();
        },

        successCallback() {
            this.endLoading('save-product');
            this.showToastrSuccess();
            this.$emit('product-saved');

            if (this.productExists) {
                this.hideModal();
            }

            if (!this.toggle) {
                this.hideModal();
            }
            this.resetForm();
        },

        removeImage() {
            this.product.media_urls = [];
            this.remove_media = true;
        },

        resetForm() {
            this.$refs.dropzone.removeAllFiles();
            this.remove_media = false;
            this.setProduct(this.attributes());
        },

        hasFileToUpload(id) {
            let files = this.$refs.dropzone.getAcceptedFiles();
            return (
                !_.isEmpty(files) && !_.isUndefined(id) && files[0].name != ''
            );
        },

        hasFileUploaded() {
            return this.$refs.dropzone.getFiles().length > 0;
        },

        hasRejectedFile() {
            return this.$refs.dropzone.getRejectedFiles().length > 0;
        },

        uploadFile(productId) {
            this.mediaId = productId;
            this.$refs.dropzone.processQueue();
        },

        showModal() {
            $(`#${this.modalId}`).modal('show');
        },

        hideModal() {
            $(`#${this.modalId}`).modal('hide');
        },

        registerEventBus() {
            this.$on('show-form', product => {
                if (this.product !== null) {
                    this.resetForm();
                }

                if (product === null) {
                    this.setProduct(this.attributes());
                    this.showModal();
                } else {
                    if (this.onLoading('fetch-product')) {
                        return;
                    }

                    this.getProduct(product.id)
                        .then(res => {
                            const product = res.data.data;

                            if (product.variants.length) {
                                const sortedVariants = this.sortVariantBySequence(
                                    product.variants,
                                );
                                product.variants = sortedVariants;
                            }

                            this.setProduct(product);
                            this.showModal();
                        })
                        .catch(err => this.showApiError(err));
                }
            });
        },

        attributes() {
            return {
                code: '',
                name: '',
                outlet_id: this.activeOutlet
                    ? this.activeOutlet
                    : this.firstAvailableOutlet,
                description: '',
                variants: [
                    {
                        id: null,
                        code: null,
                        name: 'Regular',
                        cost: '0.00',
                        price: '0.00',
                        default: true,
                        sequence: 1,
                    },
                ],
                price_type: this.initialPriceType,
                /*
                color_code: null,
                */
                modifier_sets: [],
                tags: [],
                category: null,
                taxes: this.taxAutoAssignProductList,
                unit_price: '0.00',
                cost: null,
                uom: null,
                pack_uom: null,
                pack_quantity: null,
                media_urls: [],
                status: 1,
                chargeable: true,
                // track: true,
            };
        },
    },
};
</script>
<style>
.dz-container {
    display: flex;
}

.dz-spec-text {
    margin-top: 4px;
    font-size: 0.65rem;
    color: #6c757d;
}

.pro-tip-container {
    font-size: 11px;
}

.pro-tip-container h6 {
    font-size: 12px;
}

.pro-tip-container ul {
    padding-left: 15px;
}
</style>



// WEBPACK FOOTER //
// resources/assets/js/franchise_component/inventory/item/vuetableitem_form.vue